import { PrismicNextImage } from '@prismicio/next';
import { asText, Content, isFilled } from '@prismicio/client';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';

import {
  Card,
  Badge,
  Heading,
  CardMeta,
  CardBody,
  CardHero,
  CardSlides,
  CardFooter,
  Typography,
  CardContent,
  CardFeature,
} from '@preventx/oxygen';

// Constants
import { PriceReplacementKey } from '@/constants/configuration';

// Services
import { retrieveProductsConfiguration } from '@/services/pages/products/products';

// Components
import { Link } from '@/components/Actions/Link';
import { ProductMetadata } from '@/components/Display/ProductMetadata';

type WithFeaturedProductsContainerProps = {
  sliceId: string;
  products: Content.ProductsSliceDefaultPrimaryProductsItem[];
};

export const WithFeaturedProductsContainer = async ({
  sliceId,
  products,
}: WithFeaturedProductsContainerProps) => {
  if (products.length === 0) {
    return null;
  }

  const items = (await retrieveProductsConfiguration(products)).filter(
    (item) => item !== null,
  );

  return (
    <CardSlides addFlow>
      {items.map(({ id, link, data, price, imageMeta, colourScheme }) => {
        const metadata = data.product_metadata;
        const metadataTitle = data.product_metadata_title;

        return (
          <CardFeature key={`region-product-features-${sliceId}-${id}`}>
            <Card
              addSlideCard
              component="article"
              colour={colourScheme ?? undefined}
            >
              <CardHero component="div">
                <PrismicNextImage field={data.product_image} />

                {isFilled.richText(imageMeta.label) && (
                  <CardMeta>
                    <Badge
                      variant="solid"
                      colour="primary"
                      icon={
                        imageMeta.icon
                          ? { definition: imageMeta.icon, position: 'left' }
                          : undefined
                      }
                    >
                      {asText(imageMeta.label)}
                    </Badge>
                  </CardMeta>
                )}
              </CardHero>
              <CardBody>
                <CardContent addFlow>
                  <Heading component="h2" className="card__heading">
                    {asText(data.product_name)}
                  </Heading>

                  <Typography
                    component="p"
                    weight="font-bold"
                    margin={{ mt: 'mt-2' }}
                  >
                    {asText(data.price_label).replace(
                      PriceReplacementKey,
                      price.toString(),
                    )}
                  </Typography>

                  {isFilled.richText(data.content) && (
                    <Typography component="p" className="lede">
                      {asText(data.content)}
                    </Typography>
                  )}

                  {metadata.length > 0 && (
                    <ProductMetadata
                      id={id}
                      metadata={metadata}
                      title={metadataTitle}
                    />
                  )}
                </CardContent>

                <CardFooter>
                  <Link
                    href={link}
                    as="default"
                    type="button"
                    variant="solid"
                    retainSearchParams
                    icon={{
                      position: 'right',
                      definition: faArrowRight,
                      addDirectionalIcon: true,
                    }}
                  >
                    {link.text}
                  </Link>
                </CardFooter>
              </CardBody>
            </Card>
          </CardFeature>
        );
      })}
    </CardSlides>
  );
};
